import { useCallback, useMemo, useState } from "react";
import { useServices } from "@/hooks/useApi";
import type { CreateServiceRequest } from "@/types/api";
import { scannableTargets } from "@/utils/providerCapabilities";
import { type FilterRow, ScannerFields } from "./ScannerFields";
import type { SelectOption } from "./SelectDropdown";
import { DEFAULT_PAGE_SIZE, toScannerPayload } from "./scanBound";

/** Add, edit and remove filter rows, keyed by identity rather than position. */
function useFilterRows() {
  // One empty row from the start, as in the HTTP Source routes list; an
  // unfilled row is dropped from the payload, so it costs nothing to show.
  const [filters, setFilters] = useState<FilterRow[]>([
    { id: 0, field: "", value: "" },
  ]);

  const setFilter = useCallback((id: number, patch: Partial<FilterRow>) => {
    setFilters((rows) =>
      rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }, []);

  const addFilter = useCallback(() => {
    setFilters((rows) => {
      const nextId =
        rows.length === 0 ? 0 : Math.max(...rows.map((r) => r.id)) + 1;
      return [...rows, { id: nextId, field: "", value: "" }];
    });
  }, []);

  const removeFilter = useCallback((id: number) => {
    setFilters((rows) => rows.filter((row) => row.id !== id));
  }, []);

  return { filters, setFilter, addFilter, removeFilter };
}

/**
 * The services a scanner may target: MongoDB and PostgreSQL. Filtering the
 * list rather than validating the choice means an impossible target is never
 * offered.
 */
function useScannableTargets() {
  const { data: services, isLoading } = useServices();

  const options = useMemo<SelectOption<string>[]>(
    () =>
      scannableTargets(services ?? []).map((s) => ({
        value: s.name,
        label: s.name,
      })),
    [services],
  );

  return { options, isLoading };
}

export function useScannerFields() {
  const [targetService, setTargetService] = useState("");
  const [orderBy, setOrderBy] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pageSize, setPageSize] = useState(String(DEFAULT_PAGE_SIZE));

  const { filters, setFilter, addFilter, removeFilter } = useFilterRows();
  const targets = useScannableTargets();

  const toPayload = useCallback(
    (): Partial<CreateServiceRequest> =>
      toScannerPayload({
        targetService,
        orderBy,
        from,
        to,
        pageSize,
        filters,
      }),
    [targetService, orderBy, from, to, pageSize, filters],
  );

  const fields = (
    <ScannerFields
      targetService={targetService}
      setTargetService={setTargetService}
      targetOptions={targets.options}
      targetsLoading={targets.isLoading}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      from={from}
      setFrom={setFrom}
      to={to}
      setTo={setTo}
      pageSize={pageSize}
      setPageSize={setPageSize}
      filters={filters}
      setFilter={setFilter}
      addFilter={addFilter}
      removeFilter={removeFilter}
    />
  );

  return { fields, toPayload };
}
